import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AOS from 'aos';
import '../styles/pages/Schrodinger.css';
import Navbar from '../components/layout/Navbar';
import SchrodingerHeader from '../components/assets/Schrodinger/Schrodinger_Header.png';
import LiveDesignLogo from '../components/assets/Schrodinger/LiveDesign_Logo.png';
import LiveDesignScreen from '../components/assets/Schrodinger/LiveDesign_screen.png';
import LiveDesignRoadmap from '../components/assets/Schrodinger/UX_LiveDesign_Roadmap.png';
import FreeformColumn from '../components/assets/Schrodinger/Freeform_column.png';
import FormulaBuilder from '../components/assets/Schrodinger/Formula_builder.png';
import ColoringRules from '../components/assets/Schrodinger/Coloring_rules.png';
import EntityGroupingManager from '../components/assets/Schrodinger/Entity_grouping_manager.png';
import ImportImg from '../components/assets/Schrodinger/Import.png';
import CompositeRow from '../components/assets/Schrodinger/Composite_row.png';
import MaestroLogo from '../components/assets/Schrodinger/Maestro_logo.png';
import MaestroScreen from '../components/assets/Schrodinger/Maestro_screen.png';
import CSP from '../components/assets/Schrodinger/CSP.png';
import KinaseSelectivity from '../components/assets/Schrodinger/Kinase_selectivity.png';

/**
 * The gate is off. Set this back to `true` to restore it — that is the
 * only change needed. The password, the modal, the recruiter message
 * and the submit handler below all stay in place and keep working.
 *
 * CaseStudies.js imports this flag to decide whether the Schrödinger
 * card shows its "Password protected" badge, so the page and the card
 * can't fall out of step.
 *
 * Turned off on 2026-10-12: the page was drawing 210 views at a 4s
 * average engagement, the lowest on the site — visitors were landing
 * and bouncing off the gate rather than reading.
 */
export const PASSWORD_PROTECTED = false;

const PASSWORD = '1224783183';

const recruiterMessage = (
  <div className="schrodinger-password-message">
    <strong>Recruiter?</strong>
    <p style={{marginTop: '0.5rem', marginBottom: 0}}>
      If you don't have the password, you can find it in my resume—either next to the portfolio link or under the Schrödinger experience section.<br/>
      <p style={{color: 'var(--accent-color,rgb(125, 125, 125))', fontSize: '0.97em', paddingTop: '2px', marginBottom: 0}}>
        If you're referring to an older version of my resume, feel free to email me at <a className="schrodinger-link" href='mailto:mchidrupa01@gmail.com' style={{color: 'var(--accent-color,rgb(125, 125, 125))', fontSize: '0.97em'}}>mchidrupa01@gmail.com</a>, and I'll send it to you right away.
      </p>
    </p>
  </div>
);

const sectionCount = 8; // Updated to match the actual number of major sections after cleanup

const Schrodinger = () => {
  const [input, setInput] = useState('');
  // Starts unlocked while the gate is off, so the modal branch below
  // never renders and every effect gated on `unlocked` runs as normal.
  const [unlocked, setUnlocked] = useState(!PASSWORD_PROTECTED);
  const [error, setError] = useState('');
  const [selectedLiveDesignProject, setSelectedLiveDesignProject] = useState('Freeform Column Dialog');
  const [selectedMaestroProject, setSelectedMaestroProject] = useState('Crystal Structure Prediction');
  const [liveDesignActiveTab, setLiveDesignActiveTab] = useState('wireframes');
  const [maestroActiveTab, setMaestroActiveTab] = useState('wireframes');
  const [expandedLiveDesignProject, setExpandedLiveDesignProject] = useState('Freeform Column Dialog');
  const [expandedMaestroProject, setExpandedMaestroProject] = useState('Crystal Structure Prediction');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 700);
  const progressRef = useRef(null);
  const progressMobileRef = useRef(null);
  const navigate = useNavigate();

  const liveDesignProjects = [
    {
      name: 'Freeform Column Dialog',
      wireframe: FreeformColumn,
      overview: {
        problem: 'The existing flow for creating single- and multi-valued picklists was clunky and confusing. Users struggled with locating relevant options, managing list values, and understanding the implications of their selections. Error messages were often disruptive, and guidance throughout the process was limited or unclear.',
        goals: [
          'Simplify the creation and management of picklists',
          'Improve overall form usability and structure',
          'Enhance guidance and reduce user friction',
          'Make error handling more intuitive and less penalizing'
        ],
        role: ['Research', 'User Flows', 'Wireframing', 'High Fidelity', 'Dev Handoff', 'QA']
      },
      results: {
        outcome: 'The redesign significantly improved the overall user experience by reducing cognitive load, increasing efficiency, and minimizing friction. Users reported better clarity and control while configuring picklists, and internal stakeholders observed a noticeable drop in support requests related to form usability.',
        improvements: [
          'Redesigned Flow: Introduced a streamlined process for creating both single- and multi-valued picklists, making it easier to add, edit, and delete values.',
          'Improved Form Structure: Reordered fields and options in the form to follow a more natural, intuitive flow.',
          'Value Reordering: Enabled users to easily reorder dropdown and picklist values using drag-and-drop interactions.',
          'Enhanced Guidance: Refined tooltips and labels to offer clearer, contextual help at every step.',
          'User-Friendly Error Handling: Redesigned error messages to be more informative and less disruptive, allowing users to correct issues without losing progress.',
          'Earlier Data Type Selection: Moved the data type selection to the first step, helping users make more informed decisions early in the process.'
        ]
      }
    },
    {
      name: 'Formula Builder Dialog',
      wireframe: FormulaBuilder,
      overview: {
        problem: 'The original Formula Builder dialog was functional but unintuitive. Users struggled to locate columns and functions, understand syntax, and visually debug formulas. The interface lacked clarity and helpful affordances, making formula creation a slow and error-prone task — especially for complex expressions.',
        goals: [
          'Improve discoverability of columns, functions, and operators',
          'Enhance the expression-building experience',
          'Reduce cognitive load through visual cues and guidance',
          'Introduce interactive editing and in-context support'
        ],
        role: ['Research', 'High Fidelity', 'Dev Handoff', 'QA']
      },
      results: {
        outcome: 'The redesign significantly improved the usability of the Formula Builder. Users now spend less time searching, debugging, and reworking expressions. The visual cues, interactivity, and real-time support have made formula creation both faster and more accurate.',
        improvements: [
          'Searchable Formula Inputs: Users can now search for columns and functions, making it faster and easier to build expressions without needing to scroll or remember exact names.',
          'Operator Keypad Enhancements: A visual operator keypad displays all supported formula operations, giving users immediate feedback on what\'s available.',
          'Quick Access to Common Functions: Frequently used functions are added directly to the keypad for faster access',
          'Focused Expression Field: The formula input field is now the central focus of the dialog, with more space and improved interactivity for building and editing formulas.',
          'Parenthesis Coloring: Visual cues like colored parentheses help users identify syntax errors and ensure formulas are balanced.',
          'Interactive Bubble Editing: Newly introduced "bubbles" allow users to click and change column references directly within the text area, streamlining updates.',
          'In-Context Function Help: Function syntax appears as a collapsible tooltip when users type inside parentheses — offering guidance without cluttering the interface.'
        ]
      }
    },
    {
      name: 'Coloring Rules Dialog',
      wireframe: ColoringRules,
      overview: {
        problem: 'The earlier coloring rules interface offered limited rule types and lacked the flexibility users needed to define specific conditions. It wasn\'t easy to cover edge cases or toggle visibility, and the interface often felt cluttered and restrictive for power users.',
        goals: [
          'Expand rule types for both text and numeric data',
          'Improve control and customization for users',
          'Make the interface cleaner and more intuitive',
          'Ensure support for edge cases and error states'
        ],
        role: ['Research', 'User Flows', 'Wireframing', 'High Fidelity', 'Dev Handoff', 'QA']
      },
      results: {
        outcome: 'The updated coloring rules experience empowers users to define more accurate and comprehensive conditions with ease. Feedback highlighted improved clarity, better control over rule management, and appreciation for the cleaner interface.',
        improvements: [
          'Expanded Text-Based Rules: Introduced new conditions like begins with, ends with, and contains, allowing users to define more specific and meaningful text-based criteria.',
          'Improved Value-Based Logic: Added comparison options such as <, >, and equals, enabling users to set clear, precise numerical thresholds.',
          'Support for Edge Cases: Users can now create rules for special scenarios like empty values or failed entries, ensuring broader and more accurate rule coverage.',
          'Switch Between Rule Types: Users can seamlessly toggle between categorical and range-based rules based on their data needs.',
          'Cleaner UI: The delete option now appears only on hover, reducing visual clutter and helping users focus on rule content.'
        ]
      }
    },
    {
      name: 'Entity Grouping Manager',
      wireframe: EntityGroupingManager,
      overview: {
        problem: 'The Entity Grouping Manager is a powerful tool that allows users to build complex composite entities — such as antibodies composed of chains, or OLED devices composed of layers, which in turn contain materials. The tool plays a crucial role in modeling hierarchical relationships accurately and efficiently. The earlier version of the tool lacked clarity, flexibility, and error prevention mechanisms. Users found it difficult to differentiate between entity types, manage nested relationships, or confidently create groups without mistakes. The user interface also presented too much information at once, adding to the cognitive load.',
        goals: [
          'Introduce structure and visual hierarchy for better navigation and visual clutter without losing necessary detail',
          'Make it easier to identify different entity types and statuses',
          'Reduce the margin for error while grouping complex entities',
          'Improve the usability of entity selection and drag-and-drop interactions'
        ],
        role: ['Research', 'User Flows', 'Wireframing', 'High Fidelity', 'Dev Handoff', 'QA']
      },
      results: {
        outcome: 'The redesigned Entity Grouping Manager significantly improves user confidence and efficiency when building complex entity structures. Visual clarity, improved interaction design, and error prevention features led to fewer mistakes, faster task completion, and a smoother experience overall.',
        improvements: [
          'Visual Differentiation by Status: Entities now have clear visual indicators for their status — such as disabled, uneditable, or draft. This helps users group with intent and avoids confusion.',
          'Template-Driven Grouping: Users can now create templates with pre-defined structures for different types of entity groups. These templates come with placeholder classes for child entities, enabling a fill-in-the-blanks approach that minimizes errors.',
          'Direct Import from LiveReports: A new feature allows users to either drag entities from their report into the tool or use a "Get Selected Entities" CTA to import them directly, speeding up the workflow.',
          'Smart Drag-and-Drop with Visual Cues: Visual guidance shows users where entities can be dropped. Invalid drop zones turn red to indicate errors, preventing misplacement before it happens.',
          'Collapsible Hierarchies: Instead of always showing the entire hierarchy, we introduced collapsible sections using chevrons. Users can now expand or collapse entities as needed, reducing information overload.',
          'Improved Access to Key Actions: The "Add New Entities" button is now placed prominently at the top, making it easier to locate and reducing unnecessary scrolling.'
        ]
      }
    },
    {
      name: 'Import Dialog',
      wireframe: ImportImg,
      overview: {
        problem: 'The Import Panel was originally designed to help users import various types of entities from external formats. As we introduced complex entities into the platform—such as composite structures with parent-child relationships—it became essential to evolve the panel to support this advanced data model without compromising usability or data integrity. Importing complex entities introduced several layers of complexity: Each entity contained both relationship data (defining how it connects to its parent) and metadata (describing its properties). Users needed more control over how data was mapped, modified, and validated. Risk of data overwrites or incorrect imports was high, especially when working with custom IDs and entity structures.',
        goals: [
          'Support the import of complex entities, including their relationship data.',
          'Give users full control over column mapping, ID assignment, and conflict resolution.',
          'Prevent irreversible data loss or import errors through strict validation and clear warnings.'
        ],
        role: ['Research', 'User Flows', 'Wireframing', 'High Fidelity', 'Dev Handoff', 'QA']
      },
      results: {
        outcome: 'The redesigned import experience offers greater flexibility while enforcing stricter safeguards. Users can confidently import even the most complex data structures with clear visibility into what is being added, changed, or blocked. The stepwise flow, enhanced ID management, and robust conflict resolution tools significantly improved accuracy, trust, and usability.',
        improvements: [
          'Mapping Relationship Data and Metadata: The import flow now clearly distinguishes between relationship data (e.g., parent-child mappings) and metadata (e.g., names, types, values). Required columns for both types are identified upfront. Users can manually specify whether a column should be treated as relationship data, giving them more control and reducing misclassification.',
          'User-Defined ID Handling: Users now have the ability to edit or assign custom IDs directly within the import panel. This helps prevent ID conflicts and provides a proactive way to resolve issues before finalizing the import.',
          'Conflict Resolution Before Import: A dedicated step in the flow allows users to handle conflicts with existing data. They can choose to skip records that already exist, append new data without overwriting, or override existing entries with new values. To avoid irreversible mistakes, we introduced hard stops—for example, imports are blocked if an existing structure is mapped to an ID. This ensures that no structural relationships are overwritten unintentionally.'
        ]
      }
    },
    {
      name: 'Composite Rows',
      wireframe: CompositeRow,
      overview: {
        problem: 'The addition of composite rows—where entities can have nested child entities—was a major evolution in the system\'s data model. This shift required rethinking how users search and filter data, especially when dealing with complex, hierarchical conditions across parent and child entities. Standard filtering logic was insufficient for advanced cases like: "Find entities where child 1 has property A and property B, and child 2 has property B or property C." The existing search system only supported flat logic—either all AND or all OR—making it impossible to define conditions involving nested logical groupings across child entities.',
        goals: [
          'Allow users to express advanced logical relationships across parent-child entity structures',
          'Keep the experience intuitive in both simple and power-user workflows',
          'Provide real-time clarity on how filters are interpreted and applied'
        ],
        role: ['Research', 'User Flows', 'Wireframing', 'High Fidelity', 'Dev Handoff', 'QA']
      },
      results: {
        outcome: 'The addition of query grouping transformed the search experience into a flexible and scalable tool for working with hierarchical data. Both novice and expert users can now construct precise, logical queries—whether through a simple visual interface or a power-user expression editor. The improvements were critical in enabling workflows across research and development teams that rely on highly nuanced data filtering.',
        improvements: [
          'Two Modes of Query Building: Simple View (guided interface for grouped logic) and Complex View (manual expression editor with full flexibility).',
          'Introduction of Query Groups: Users can now create Query Groups, each representing a logical block of conditions, set to match parent or child properties.',
          'Flexible Query Mapping: Users can start by creating a group and then add conditions inside it, or start with a condition and assign it to an existing group.',
          'Intuitive Editing Experience: Easy to create, modify, or delete both groups and individual conditions through a clear interface.',
          'Tooltips and Guidance: Contextual tooltips explain the difference between matching by parent or child properties, helping users avoid misinterpretation.'
        ]
      }
    }
  ];

  const maestroProjects = [
    {
      name: 'Crystal Structure Prediction',
      wireframe: CSP,
      overview: {
        problem: 'Users working with crystal structure predictions needed a way to visualize and explore large datasets. While a separate panel already existed to generate and compute data for crystal structures, users wanted to plot this data against different variables and, crucially, be able to identify which specific entity a data point represented—something that wasn\'t possible in the earlier version.',
        contribution: 'To address this, we designed a new viewer panel with two key modes: a data table view for precise, tabular inspection and a scatterplot visualization tool for graphical exploration. Users can switch between the two views, select custom x and y axes, and reset axis ranges to focus on different parts of the dataset. To support datasets with a large number of points, we also ensured the system was responsive and performant. Selecting any data point in the scatterplot triggers a link to the Maestro workspace, displaying the corresponding entity in real time. This tight integration between data and structure view was a critical feature for users to explore the scientific meaning behind the numbers.',
        role: ['Research', 'User Flows', 'Wireframing', 'High Fidelity', 'Dev Handoff', 'QA'],
        outcome: 'This panel empowered scientists to quickly identify patterns, correlate structures with properties, and explore their predictions in a far more intuitive and efficient way.'
      }
    },
    {
      name: 'Kinase Selectivity',
      wireframe: KinaseSelectivity,
      overview: {
        problem: 'Kinases play a key role in cellular signaling pathways and are often drug targets. However, due to structural similarities between kinases, drugs may unintentionally interact with off-target kinases, leading to side effects or reduced efficacy. Scientists needed a way to visualize and compare residues across kinase families to assess selectivity more accurately.',
        contribution: 'We designed a Kinase Selectivity Panel that allows scientists to explore residue-level differences between the target kinase and other members of the kinase family. To begin, users set a ligand and receptor chain, adjust parameters like distance cutoff and angle cutoff to define the comparison range. Once configured, the panel returns a data table listing residues across kinases.',
        features: 'Residue Selection: Clicking on any residue in the table triggers a visualization panel on the right. Pie Chart View: This shows residue frequency at the selected position across all comparable kinases. Conservation Metrics: Each position also has a conservation score and category to quickly assess variability. Hover Insights: Hovering on the pie chart reveals additional info—like the number of kinases containing a specific residue at that position.',
        role: ['Research', 'User Flows', 'Wireframing', 'High Fidelity', 'Dev Handoff', 'QA'],
        outcome: 'This panel empowered scientists to quickly identify patterns, correlate structures with properties, and explore their predictions in a far more intuitive and efficient way.'
      }
    }
  ];

  const selectedLiveDesignProjectData = liveDesignProjects.find(p => p.name === selectedLiveDesignProject);
  const selectedMaestroProjectData = maestroProjects.find(p => p.name === selectedMaestroProject);

  useEffect(() => {
    if (!unlocked) return;
    const handleScroll = () => {
      const sections = document.querySelectorAll('.schrodinger-content section, .schrodinger-content > div[data-aos]');
      let viewed = 0;
      const buffer = window.innerHeight * 0.3;
      sections.forEach(section => {
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight - buffer) viewed++;
      });
      const percent = Math.max(0, Math.min(1, viewed / sectionCount));
      if (progressRef.current) {
        progressRef.current.style.height = `${percent * 100}%`;
      }
      if (progressMobileRef.current) {
        if (window.innerWidth <= 700) {
          progressMobileRef.current.style.width = `${percent * 100}%`;
        }
      }
    };
    
    // Prevent automatic scrolling on page load
    window.scrollTo(0, 0);
    
    window.addEventListener('scroll', handleScroll);
    // Delay the initial handleScroll call to prevent immediate scroll behavior
    setTimeout(() => {
      handleScroll();
    }, 100);
    
    // Fix AOS animation
    AOS.refresh();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [unlocked]);

  useEffect(() => {
    AOS.init({ once: false, duration: 700, easing: 'ease-out-cubic' });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 700;
      setIsMobile(newIsMobile);
      console.log('Mobile state:', newIsMobile, 'Window width:', window.innerWidth);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input === PASSWORD) {
      setUnlocked(true);
      setError('');
    } else {
      setError('Incorrect password. Please try again.');
    }
  };

  const handleLiveDesignProjectSelect = (projectName) => {
    setSelectedLiveDesignProject(projectName);
    setExpandedLiveDesignProject(projectName);
  };

  const handleMaestroProjectSelect = (projectName) => {
    setSelectedMaestroProject(projectName);
    setExpandedMaestroProject(projectName);
  };

  console.log('Rendering Schrodinger - isMobile:', isMobile, 'selectedLiveDesignProject:', selectedLiveDesignProject, 'selectedMaestroProject:', selectedMaestroProject);

  if (!unlocked) {
    return (
      <div className="schrodinger-password-modal-bg">
        <div className="schrodinger-password-modal-card">
          <h2 className="schrodinger-password-title">Password Protected Page</h2>
          <div className="schrodinger-password-divider" />
          <div className="schrodinger-password-instructions">
          </div>
          {recruiterMessage}
          <form onSubmit={handleSubmit} className="schrodinger-password-form">
            <input
              type="password"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Enter password"
              className="schrodinger-password-input"
              autoFocus
            />
            <button type="submit" className="schrodinger-password-button design-system-gradient-btn">Unlock</button>
          </form>
          <button className="schrodinger-password-cancel" type="button" onClick={() => navigate('/')}>Cancel</button>
          {error && <div className="schrodinger-password-error">{error}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="schrodinger-page">
      <Navbar />
      {/* Progress indicator */}
      <div className="schrodinger-progress-indicator">
        <div ref={progressRef} style={{width: '100%', height: 0}}></div>
      </div>
      <div className="schrodinger-progress-indicator-mobile"><div ref={progressMobileRef} style={{width: 0}}></div></div>
      <img src={SchrodingerHeader} alt="Schrodinger Banner" className="schrodinger-header" data-aos="fade-up" />
      <div className="schrodinger-content">
        {/* Section 1: Intro */}
        <section data-aos="fade-up" style={{paddingBottom: '50px', marginBottom: '50px'}}>
          <h1>From Code to Creativity: My Journey into UX at Schrödinger</h1>
          <p>After working as a developer, I realized that while debugging and seeing error-free code run was fulfilling, something was missing — creativity. I wanted my work to reflect not just logic, but imagination and user-centered thinking. That's when I decided to pause, shift gears, and pursue a master's degree in Human-Computer Interaction at Arizona State University.</p>
          <p>Soon after graduating, I landed a UX internship at Schrödinger — a dream opportunity during a time when the job market was uncertain and highly competitive. Even before my internship ended, I was offered a full-time role. Schrödinger took a chance on me when I was beginning to second-guess my own chances, and for the next 1.5 years, I had the space to grow, sharpen my skills, and contribute meaningfully.</p>
          <p>Schrodinger has multiple products. I primarily worked on LiveDesign and Maestro. Here's a glimpse into the work I did. Please note that these are only the projects I'm allowed to share, as some of the work remains proprietary.</p>
          <p>Coming from a non-scientific background, I initially worried that not having formal training in chemistry or biology might hold me back — especially at a domain-heavy company like Schrödinger. But that never became a blocker. I leaned into active listening, curiosity, and asking the right questions. I regularly worked with scientists, product managers, and application experts to understand complex concepts and break them down into usable, intuitive interfaces. Over time, I realized that a strong UX mindset — empathy, clarity, and problem-solving — was just as critical as domain knowledge. And in fact, my outsider perspective often helped uncover assumptions others hadn't questioned.</p>
        </section>
        {/* Section 2: UX in LiveDesign */}
        <section data-aos="fade-up" style={{paddingBottom: '50px', marginBottom: '50px', borderBottom: '1px solid #ececec'}}>
          <img src={LiveDesignRoadmap} alt="LiveDesign Roadmap" className="schrodinger-section-img" style={{maxWidth: '800px', margin: '40px auto 32px auto'}} />
        </section>

        {/* Section 3: LiveDesign Intro */}
        <section data-aos="fade-up" className="schrodinger-two-col" style={{paddingTop: '50px', paddingBottom: '50px', marginBottom: '50px'}}>
          <div className="schrodinger-col-left">
            <img src={LiveDesignLogo} alt="LiveDesign Logo" style={{maxWidth: '220px', background: 'transparent'}} />
            <h2 style={{marginTop: '0px', marginBottom: '0px'}}>What is LiveDesign?</h2>
            <p style={{marginTop: '0px'}}>LiveDesign is a collaborative spreadsheet-like platform that enables chemists, biologists, and other scientists to coordinate, analyze, and share their research in real-time. Thanks to its intuitive interface and powerful capabilities, it's quickly becoming one of the most favored tools for scientific communication and project tracking. I was hired as a UX Designer specifically for LiveDesign. Though the early days were a bit bumpy due to internal restructuring, the process soon stabilized, and I became the dedicated UX point of contact for several core teams.</p>
          </div>
          <div className="schrodinger-col-right">
            <img src={LiveDesignScreen} alt="LiveDesign Screen" style={{maxWidth: 420, width: '100%', height: 'auto', objectFit: 'contain', boxShadow: 'none', background: 'transparent', borderRadius: 12, marginRight: 0, marginLeft: 'auto'}} />
          </div>
        </section>
        
       {/* LiveDesign Projects Section */}
       <section data-aos="fade-up" className="schrodinger-projects-section">
          <h2>LiveDesign Projects</h2>
          <p>Here's a comprehensive overview of the key projects I worked on in LiveDesign, showcasing the wireframes, problem statements, goals, my role, outcomes, and key improvements for each project.</p>
          
          <div className="schrodinger-projects-container">
            {/* Left Column - Project List */}
            <div className="schrodinger-projects-list">
              {liveDesignProjects.map((project) => (
                <div key={project.name} className="schrodinger-project-accordion-item">
                  <button
                    className={`schrodinger-project-item ${selectedLiveDesignProject === project.name ? 'active' : ''}`}
                    onClick={() => handleLiveDesignProjectSelect(project.name)}
                  >
                    {project.name}
                  </button>

                  {/* Mobile Accordion Content */}
                  {isMobile && (
                    <div className={`schrodinger-project-mobile-content ${expandedLiveDesignProject === project.name ? 'expanded' : ''}`}>
                      <div className="schrodinger-project-tabs">
                        <button
                          className={`schrodinger-tab ${liveDesignActiveTab === 'wireframes' ? 'active' : ''}`}
                          onClick={() => setLiveDesignActiveTab('wireframes')}
                        >
                          Wireframes
                        </button>
                        <button
                          className={`schrodinger-tab ${liveDesignActiveTab === 'overview' ? 'active' : ''}`}
                          onClick={() => setLiveDesignActiveTab('overview')}
                        >
                          Overview
                        </button>
                        <button
                          className={`schrodinger-tab ${liveDesignActiveTab === 'results' ? 'active' : ''}`}
                          onClick={() => setLiveDesignActiveTab('results')}
                        >
                          Results
                        </button>
                      </div>

                      <div className="schrodinger-project-content">
                        {liveDesignActiveTab === 'wireframes' && (
                          <div className="schrodinger-wireframe-container">
                            <img 
                              src={project.wireframe} 
                              alt={`${project.name} Wireframe`}
                            />
                          </div>
                        )}
                        {liveDesignActiveTab === 'overview' && (
                          <div className="schrodinger-overview-container">
                            <p><strong>Problem:</strong> {project.overview.problem}</p>
                            <p><strong>Goals:</strong></p>
                            <ul>
                              {project.overview.goals.map((goal, index) => (
                                <li key={index}>{goal}</li>
                              ))}
                            </ul>
                            <p><strong>My Role:</strong></p>
                            <div className="chip-row">
                              {project.overview.role.map((role, index) => (
                                <span key={index} className="chip">{role}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {liveDesignActiveTab === 'results' && (
                          <div className="schrodinger-results-container">
                            <p><strong>Outcome:</strong> {project.results.outcome}</p>
                            <p><strong>Key Improvements:</strong></p>
                            <div className="schrodinger-key-improvements" style={{fontSize: 12, color: '#23272f'}}>
                              <ul style={{margin: 0, paddingLeft: 18}}>
                                {project.results.improvements.map((improvement, index) => (
                                  <li key={index}><b>{improvement.split(':')[0]}:</b> {improvement.split(':').slice(1).join(':')}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop Project Details */}
            {!isMobile && (
              <div className="schrodinger-project-details">
                <div className="schrodinger-project-tabs">
                  <button
                    className={`schrodinger-tab ${liveDesignActiveTab === 'wireframes' ? 'active' : ''}`}
                    onClick={() => setLiveDesignActiveTab('wireframes')}
                  >
                    Wireframes
                  </button>
                  <button
                    className={`schrodinger-tab ${liveDesignActiveTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setLiveDesignActiveTab('overview')}
                  >
                    Overview
                  </button>
                  <button
                    className={`schrodinger-tab ${liveDesignActiveTab === 'results' ? 'active' : ''}`}
                    onClick={() => setLiveDesignActiveTab('results')}
                  >
                    Results
                  </button>
                </div>

                <div className="schrodinger-project-content">
                  {liveDesignActiveTab === 'wireframes' && selectedLiveDesignProjectData && (
                    <div className="schrodinger-wireframe-container">
                      <img 
                        src={selectedLiveDesignProjectData.wireframe} 
                        alt={`${selectedLiveDesignProject} Wireframe`}
                      />
                    </div>
                  )}
                  {liveDesignActiveTab === 'overview' && selectedLiveDesignProjectData && (
                    <div className="schrodinger-overview-container">
                      <p><strong>Problem:</strong> {selectedLiveDesignProjectData.overview.problem}</p>
                      <p><strong>Goals:</strong></p>
                      <ul>
                        {selectedLiveDesignProjectData.overview.goals.map((goal, index) => (
                          <li key={index}>{goal}</li>
                        ))}
                      </ul>
                      <p><strong>My Role:</strong></p>
                      <div className="chip-row">
                        {selectedLiveDesignProjectData.overview.role.map((role, index) => (
                          <span key={index} className="chip">{role}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {liveDesignActiveTab === 'results' && selectedLiveDesignProjectData && (
                    <div className="schrodinger-results-container">
                      <p><strong>Outcome:</strong> {selectedLiveDesignProjectData.results.outcome}</p>
                      <p><strong>Key Improvements:</strong></p>
                      <div className="schrodinger-key-improvements" style={{fontSize: 12, color: '#23272f'}}>
                        <ul style={{margin: 0, paddingLeft: 18}}>
                          {selectedLiveDesignProjectData.results.improvements.map((improvement, index) => (
                            <li key={index}><b>{improvement.split(':')[0]}:</b> {improvement.split(':').slice(1).join(':')}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
        

        {/* DESIGN SYSTEM SECTION - Cloudy Gradient Background, Overview, and Matrix */}
        <section>
        <div className="design-system-bg" >
          <div className="design-system-overview" data-aos="fade-up">
            <h2 style={{color:'#fff', fontWeight:700, fontSize:'1.3rem', marginBottom: '0.7em'}}>Design System</h2>
            <p style={{color:'#fff'}}><strong>Overview:</strong> LiveDesign is a highly complex scientific platform, developed over years by multiple designers and developers. As a result, it suffered from inconsistent UI patterns and fragmented design decisions across its interface. To bring visual and functional consistency to the product, I led the initiative to build a design system from scratch—something that became a cornerstone for all future design work at Schrödinger. This is how I structured my Figma document.</p>
          </div>

          {/* --- OPTION 2: Randomized Flex Matrix --- */}
          {/* Uncomment this block for Randomized Flex layout */}
          <div className="design-system-matrix design-system-random-flex">
            <div className="ds-card tall" data-aos="fade-up">
              <span className="ds-card-icon" aria-label="Getting Started" style={{marginBottom:'10px'}}>
                {/* Book/Start Icon */}
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#43A5FE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M16 2v4"/><path d="M8 2v4"/></svg>
              </span>
              <h3>Getting Started Page</h3>
              <ul>
                <li>Explains how to navigate through the file</li>
                <li>Guidelines for Designers and Developers</li>
                <li>Table of content</li>
              </ul>
            </div>
            <div className="ds-card tall" data-aos="fade-up">
              <span className="ds-card-icon" aria-label="Changelog" style={{marginBottom:'10px'}}>
                {/* History/Clock Icon */}
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#56B24D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              </span>
              <h3>Changelog</h3>
              <p>Document any new component additions, updates to existing components, new styles, and style modifications — including the date of creation and the designer responsible for the change.</p>
            </div>
            <div className="ds-card tall" data-aos="fade-up">
              <span className="ds-card-icon" aria-label="Implementation References" style={{marginBottom:'10px'}}>
                {/* Code/Reference Icon */}
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#43A5FE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M8 10h8M8 14h6"/></svg>
              </span>
              <h3>Implementation References</h3>
              <p>This page serves as a reference for developers, showcasing all existing implemented components in both light and dark themes to support efficient development. Any required changes to the current implementation have been marked accordingly.</p>
            </div>
            <div className="ds-card" data-aos="fade-up">
              <span className="ds-card-icon" aria-label="Style Guide Foundations" style={{marginBottom:'10px'}}>
                {/* Palette/Design Icon */}
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#43A5FE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="8.5" cy="10.5" r="1.5"/><circle cx="15.5" cy="10.5" r="1.5"/><circle cx="12" cy="16" r="1.5"/></svg>
              </span>
              <h3>Style Guide Foundations</h3>
              <ul>
                <li><b>Color Palette:</b> Built a full palette including: Primary, Secondary, Success, Warning, Error, and Info. Included common opacities for overlays. Designed box shadow tiers for dialogs, menus, and buttons. All colors and shadows were added as Figma styles and exported as Tailwind-compatible tokens.</li>
                <li><b>Typography:</b> Defined font families, sizes, weights, and line heights. Created Figma text styles as combinations of these variables for ease of use by designers. Tokens were shared with developers for Tailwind integration.</li>
                <li><b>Spacing & Radius Variables:</b> Standardized spacing units and border radius values. Ensured consistency across all components and layouts.</li>
              </ul>
            </div>
            <div className="ds-card" data-aos="fade-up">
              <span className="ds-card-icon" aria-label="Icon System" style={{marginBottom:'10px'}}>
                {/* Icon/Star Icon */}
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#56B24D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15 8.5 22 9.3 17 14.1 18.2 21 12 17.8 5.8 21 7 14.1 2 9.3 9 8.5 12 2"/></svg>
              </span>
              <h3>Icon System</h3>
              <ul>
                <li><b>Master Icon File:</b> Chose Material Design Icons for system-wide use. Custom icons are created with Material icon layout. Created a master file with all icons and their interactive states.</li>
                <li><b>Copy & Use Icon File:</b> Created a designer-friendly duplicate with ready-to-copy instances. Helped designers who preferred not to use the Figma Assets panel.</li>
              </ul>
            </div>
            <div className="ds-card" data-aos="fade-up">
              <span className="ds-card-icon" aria-label="Component Library" style={{marginBottom:'10px'}}>
                {/* Library/Component Icon */}
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#43A5FE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
              </span>
              <h3>Component Library</h3>
              <ul>
                <li><b>Master Components File:</b> Built 100+ reusable components in both light and dark themes. Each component included interaction states, variants, and responsiveness.</li>
                <li><b>Copy & Use Components File:</b> Provided clean, one-click instances of each component for faster design workflows. Designers could use the side panel in Figma to tweak state/variant properties without navigating the master.</li>
              </ul>
            </div>
            
          </div>
        </div>
        </section>
        

        
        

        
        {/* Maestro Intro */}
        <section data-aos="fade-up" className="schrodinger-two-col" style={{padding: '50px', marginBottom: '50px'}}>
          <div className="schrodinger-col-left">
            <img src={MaestroLogo} alt="Maestro Logo" style={{maxWidth: '100px', background: 'transparent'}} />
            <h2 style={{marginTop: '0px', marginBottom: '0px'}}>What is Maestro?</h2>
            <p style={{marginTop: '0px'}}>Maestro is a scientific software OS based platform designed to support drug discovery and molecular research. It provides an integrated environment for chemists, biologists, and computational scientists to visualize molecular structures, simulate interactions, and analyze experimental data. With powerful tools for modeling, visualization, and data interpretation, Maestro streamlines complex scientific workflows, enabling faster and more informed decision-making in pharmaceutical and life sciences research.</p>
          </div>
          <div className="schrodinger-col-right">
            <img src={MaestroScreen} alt="Maestro Screen" style={{maxWidth: 420, width: '100%', height: 'auto', objectFit: 'contain', boxShadow: 'none', background: 'transparent', borderRadius: 12, margin: '100px 0px 0px auto'}} />
          </div>
        </section>

        {/* Maestro Projects Section */}
        <section data-aos="fade-up" className="schrodinger-projects-section">
          <h2>Maestro Projects</h2>
          <p>Here's an overview of the key projects I worked on in Maestro, showcasing the wireframes and comprehensive project details including problem statements, my contributions, and outcomes.</p>
          
          <div className="schrodinger-projects-container">
            {/* Left Column - Project List */}
            <div className="schrodinger-projects-list">
              {maestroProjects.map((project) => (
                <div key={project.name} className="schrodinger-project-accordion-item">
                  <button
                    className={`schrodinger-project-item ${selectedMaestroProject === project.name ? 'active' : ''}`}
                    onClick={() => handleMaestroProjectSelect(project.name)}
                  >
                    {project.name}
                  </button>

                  {/* Mobile Accordion Content */}
                  {isMobile && (
                    <div className={`schrodinger-project-mobile-content ${expandedMaestroProject === project.name ? 'expanded' : ''}`}>
                      <div className="schrodinger-project-tabs">
                        <button
                          className={`schrodinger-tab ${maestroActiveTab === 'wireframes' ? 'active' : ''}`}
                          onClick={() => setMaestroActiveTab('wireframes')}
                        >
                          Wireframes
                        </button>
                        <button
                          className={`schrodinger-tab ${maestroActiveTab === 'overview' ? 'active' : ''}`}
                          onClick={() => setMaestroActiveTab('overview')}
                        >
                          Overview
                        </button>
                      </div>

                      <div className="schrodinger-project-content">
                        {maestroActiveTab === 'wireframes' && (
                          <div className="schrodinger-wireframe-container">
                            <img 
                              src={project.wireframe} 
                              alt={`${project.name} Wireframe`}
                            />
                          </div>
                        )}
                        {maestroActiveTab === 'overview' && (
                          <div className="schrodinger-overview-container">
                            <p><strong>Problem:</strong> {project.overview.problem}</p>
                            <p><strong>My Contribution:</strong> {project.overview.contribution}</p>
                            {project.overview.features && (
                              <p><strong>Visual Interaction & Features:</strong> {project.overview.features}</p>
                            )}
                            <p><strong>My Role:</strong></p>
                            <div className="chip-row">
                              {project.overview.role.map((role, index) => (
                                <span key={index} className="chip">{role}</span>
                              ))}
                            </div>
                            <p><strong>Outcome:</strong> {project.overview.outcome}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop Project Details */}
            {!isMobile && (
              <div className="schrodinger-project-details">
                <div className="schrodinger-project-tabs">
                  <button
                    className={`schrodinger-tab ${maestroActiveTab === 'wireframes' ? 'active' : ''}`}
                    onClick={() => setMaestroActiveTab('wireframes')}
                  >
                    Wireframes
                  </button>
                  <button
                    className={`schrodinger-tab ${maestroActiveTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setMaestroActiveTab('overview')}
                  >
                    Overview
                  </button>
                </div>

                <div className="schrodinger-project-content">
                  {maestroActiveTab === 'wireframes' && selectedMaestroProjectData && (
                    <div className="schrodinger-wireframe-container">
                      <img 
                        src={selectedMaestroProjectData.wireframe} 
                        alt={`${selectedMaestroProject} Wireframe`}
                      />
                    </div>
                  )}
                  {maestroActiveTab === 'overview' && selectedMaestroProjectData && (
                    <div className="schrodinger-overview-container">
                      <p><strong>Problem:</strong> {selectedMaestroProjectData.overview.problem}</p>
                      <p><strong>My Contribution:</strong> {selectedMaestroProjectData.overview.contribution}</p>
                      {selectedMaestroProjectData.overview.features && (
                        <p><strong>Visual Interaction & Features:</strong> {selectedMaestroProjectData.overview.features}</p>
                      )}
                      <p><strong>My Role:</strong></p>
                      <div className="chip-row">
                        {selectedMaestroProjectData.overview.role.map((role, index) => (
                          <span key={index} className="chip">{role}</span>
                        ))}
                      </div>
                      <p><strong>Outcome:</strong> {selectedMaestroProjectData.overview.outcome}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Other Projects */}
        <section data-aos="fade-up" style={{margin: '0px 0px 70px 0px', padding: '0px 0px 50px 0px'}}>
          <h2>Other Projects</h2>
          <p>Apart from the projects listed here, I also contributed to a few upcoming features that are set to be released in August this year. I'll be updating this space once those go live, as they are currently part of proprietary software.</p>
        </section>

        {/* Key Learnings */}
        <div className="design-system-bg" style={{margin: '0px', padding: '40px 0px'}}>
          <section data-aos="fade-up" style={{background: 'transparent', boxShadow: 'none', border: 'none', color: '#fff', maxWidth: '900px', margin: '0 auto'}}>
            <h2 style={{color: '#fff'}}>Key Learnings at Schrodinger</h2>
            <p style={{color: '#fff'}}>Working at Schrödinger was a crash course in designing for a deeply technical and domain-heavy environment. Coming in without a background in science or chemistry could have been intimidating — but instead, it became one of my greatest growth opportunities. I learned how to ask the right questions, collaborate closely with scientists and domain experts, and break down complex scientific workflows into intuitive design solutions.</p>
            <p style={{color: '#fff'}}>Through products like LiveDesign and Maestro, I not only honed my UX skills, but also gained a deep appreciation for the role design plays in accelerating scientific discovery. From leading large-scale redesigns like the Formula Builder and Entity Grouping tools, to establishing a scalable design system that brought visual consistency to a multi-team product — I learned how to balance user needs, technical constraints, and business goals with clarity and creativity.</p>
            <p style={{color: '#fff'}}>I also learned that good design isn't just about screens — it's about process, documentation, communication, and continuous iteration. Whether it was writing spec documents that bridged the gap between science and design, QA'ing final features, or revisiting designs based on real-world usage, every step added to my growth as a thoughtful, resilient designer.</p>
            <p style={{color: '#fff'}}>Schrödinger gave me the space to experiment, the guidance to learn fast, and the ownership to make a real impact — and those lessons will stay with me wherever I go next.</p>
          </section>
        </div>
        
      </div>
    </div>
  );
};

export default Schrodinger; 